import {
  Form,
  Row,
  Col,
  Descriptions,
  Checkbox,
  Typography,
  Spin,
  message,
  Flex,
  Divider,
} from "antd";
import { useEffect, useState } from "react";
import CustomInput from "../../../components/CustomInput/Input";
import CustomSelect from "../../../components/CustomSelect/CustomSelect";
import FormFooter from "../../../components/CustomForms/FormFooter";
import CountryCode from "../../../components/CountryCodeSelect/CountryCode";
import {
  BTN_TXT,
  ENT_DOC_CONFIG,
  ERROR_MSGS,
  TITLE_TXT,
  ENTERPRISE_SUCCESS_MESSAGES,
} from "../../../constants/constants";
import { address1Rules, address2Rules, pincodeRules, stateRules, cityRules } from "../../../helpers/helpers";
import { useDispatch, useSelector } from "react-redux";
import {
  companyNameRules,
  nameRules,
  emailRules,
  phoneNumberRules,
  panRules,
  capitalizeWords,
  gstinRules,
  websiteRules,
  CommentsRules,
  descriptionRules,
  financialSummaryRules,
} from "../../../helpers/helpers";
import {
  getDesignations,
  getEnterpriseView,
  updateErrorStatus,
  getAllRating,
  getApproveEnt,
  approveEnt,
  verifyEntPan,
  verifyEntGstin,
} from "../../../redux/slices/enterpriseSlice";
import { getDropdownData } from "../../../redux/slices/EnqMngtSlice";
import CustomDatePicker from "../../../components/CustomDatePicker/CustomDatePicker";
import dayjs from "dayjs";
import ApproveEntDocUploadCard from "../../../components/DocUploadCard/ApproveEntDocUploadCard";
import { count } from "../../../redux/slices/dealsSlice";
import CustomButton from "../../../components/CustomButton/Button";

const { Text } = Typography;

const ApproveEntForm = ({ onClose, selectedStatus, entId }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const {
    loading,
    designationsData,
    ratingData,
    error,
    approveData,
    approveSubmitLoading,
    panLoading,
    gstinLoading,
    panError,
    gstinError,
  } = useSelector((state) => state.enterprise);
  const { dropdownData } = useSelector((state) => state.enqMngt);
  const [mouChecked, setMouChecked] = useState(false);
  const [isCommSame, setIsCommSame] = useState(false);
  const formValues = Form.useWatch(undefined, form);
  const { pan, gstin, entName } = formValues || {};

  const isPanVerified = approveData?.kycDetails?.kycPanValue?.isVerified;
  const isGstinVerified = approveData?.kycDetails?.kycGstinValue?.isVerified;

  useEffect(() => {
    dispatch(getDesignations());
    dispatch(getDropdownData());
    dispatch(getAllRating());
    dispatch(getApproveEnt(entId));
    return () => {
      dispatch(updateErrorStatus());
    };
  }, [dispatch, entId]);

  useEffect(() => {
    if (approveData) {
      form.setFieldsValue({
        entName: approveData.entName,
        pocEmail: approveData.email,
        entWebsite: approveData?.website?.replace(/^https?:\/\//i, ""),
        industryType: approveData.industryTypeId,
        rating: approveData.ratingId,
        pocName: approveData.pocName,
        pocPhoneNumber: approveData.phoneNumber,
        pan: approveData.pan,
        gstin: approveData.gstin,
        incorporationDate: dayjs(approveData.incorporationDate),
        entDesc: approveData.description,
        finSummary: approveData.financialSummary,
        comments: approveData.comments,
        pocDesignation: approveData.designationId,
        // Add commas
        creditExposure: approveData.creditExposure
          ? Number(approveData.creditExposure).toLocaleString("en-IN")
          : "",
      });
      setMouChecked(approveData.isMouSigned);
    }
  }, [approveData, form, mouChecked]);

  const onFileChange = (key, file) => { };

  const handleSubmit = async (values) => {
    const payload = {
      entId: entId,
      industryTypeId: values.industryType,
      incorporationDate: values.incorporationDate,
      description: values.entDesc,
      financialSummary: values.finSummary,
      website: values.entWebsite?.trim()
        ? `https://${values.entWebsite.trim().replace(/^https?:\/\//i, "")}`
        : null,
      comments: values.comments,
      // Remove commas before sending
      creditExposure: Number(values.creditExposure?.replace(/,/g, "")),
      communicationAddress: {
        addressLine1: isCommSame
          ? approveData?.primaryAddress?.addressLine1
          : values.commAdd1,
        addressLine2: isCommSame
          ? approveData?.primaryAddress?.addressLine2
          : values.commAdd2,
        pinCode: isCommSame
          ? approveData?.primaryAddress?.pinCode
          : values.commPincode,
        city: isCommSame ? approveData?.primaryAddress?.city : values.commCity,
        state: isCommSame
          ? approveData?.primaryAddress?.state
          : values.commState,
      },
    };

    const result = await dispatch(approveEnt(payload));
    if (result.payload.statusCode === 201) {
      message.success(
        ENTERPRISE_SUCCESS_MESSAGES.approve(
          approveData?.entName || values?.entName || "Enterprise",
        ),
      );
      dispatch(getEnterpriseView(selectedStatus));
      dispatch(count("ENTERPRISE"));
      onClose();
      dispatch(updateErrorStatus());
    }
  };

  const primaryAddItems = [
    {
      key: "addressLine1",
      label: "Address 1",
      children: approveData?.primaryAddress?.addressLine1,
    },
    {
      key: "addressLine2",
      label: "Address 2",
      children: approveData?.primaryAddress?.addressLine2,
    },
    { key: "city", label: "City", children: approveData?.primaryAddress?.city },
    {
      key: "pinCode",
      label: "Pincode",
      children: approveData?.primaryAddress?.pinCode,
    },
    {
      key: "state",
      label: "State",
      children: approveData?.primaryAddress?.state,
    },
  ];

  const handleCommSameChange = (e) => {
    const checked = e.target.checked;
    setIsCommSame(checked);
    if (form) {
      if (checked) {
        form.setFieldsValue({
          commAdd1: approveData?.primaryAddress?.addressLine1,
          commAdd2: approveData?.primaryAddress?.addressLine2,
          commCity: approveData?.primaryAddress?.city,
          commState: approveData?.primaryAddress?.state,
          commPincode: approveData?.primaryAddress?.pinCode,
        });
      } else {
        form.setFieldsValue({
          commAdd1: "",
          commAdd2: "",
          commCity: "",
          commState: "",
          commPincode: "",
        });
      }
    }
  };

  const filteredFiles = () => {
    if (!Array.isArray(ENT_DOC_CONFIG)) return [];
    let files = [...ENT_DOC_CONFIG];
    if (!mouChecked) {
      files = files.filter((file) => file.key !== "mou");
    }
    return files;
  };

  const mapKycData = (key) => {
    if (
      !approveData ||
      !approveData.kycDetails ||
      !approveData.kycDetails[key]
    ) {
      return {};
    }
    const doc = approveData.kycDetails[key];
    return {
      fileUrl: doc?.docUrl,
      fileName: doc?.docUrl
        ? decodeURIComponent(doc.docUrl.split("/").pop())
        : undefined,
      isVerified: doc?.isVerified,
      rejected: doc?.rejected,
      docId: doc?.docId,
    };
  };

  const handleVerifyBoth = async () => {
    if (!entId) return;

    let refreshRequired = false;

    if (!approveData?.kycDetails?.kycPanValue?.isVerified) {
      const panResponse = await dispatch(verifyEntPan({ entName, pan, entId }));

      if (panResponse?.payload?.statusCode !== 201) {
        return;
      }

      refreshRequired = true;
    }

    const gstinResponse = await dispatch(verifyEntGstin({ gstin, entId }));

    if (gstinResponse?.payload?.statusCode === 201) {
      refreshRequired = true;
    }

    if (refreshRequired) {
      dispatch(getApproveEnt(entId));
    }
  };

  return (
    <Spin spinning={loading}>
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        initialValues={{ remember: true, countryCode: "+91" }}
        className="approve-ent-form"
      >
        <Row gutter={[24, 0]}>
          <Col xs={24} md={12}>
            <CustomInput
              label="Enterprise Name"
              name="entName"
              type="text"
              disabled={isPanVerified}
              placeholder="Enter your Enterprise Name"
              rules={companyNameRules("Enterprise")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="POC Name"
              name="pocName"
              type="text"
              disabled
              placeholder="Enter POC Name"
              rules={nameRules("POC")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="POC Email"
              name="pocEmail"
              type="email"
              disabled
              placeholder="Enter POC email"
              rules={emailRules("POC")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="POC Phone Number"
              name="pocPhoneNumber"
              disabled
              addonBefore={<CountryCode disabled />}
              placeholder="Phone Number"
              rules={phoneNumberRules("POC")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomSelect
              label="POC Designation"
              name="pocDesignation"
              showSearch
              disabled
              placeholder="Select Designation"
              options={designationsData?.map((desig) => ({
                value: desig.designationId,
                label: capitalizeWords(desig.designationName),
              }))}
              rules={[
                { required: true, message: ERROR_MSGS.DESIGNATION_ERR_MEG },
              ]}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomSelect
              label="Industry Type"
              name="industryType"
              showSearch
              disabled
              placeholder="Select Industry Type"
              options={dropdownData?.industryTypeList?.map((industry) => ({
                value: industry.industryTypeId,
                label: capitalizeWords(industry.industryTypeName),
              }))}
              rules={[{ required: true, message: ERROR_MSGS.SEL_IND_TYPE }]}
            />
          </Col>
          <Col xs={24}>
            <Row
              gutter={[24, 0]}
              className={
                (!isPanVerified || !isGstinVerified) && "verification-card"
              }
            >
              <Col xs={24} md={12}>
                <CustomInput
                  label="PAN"
                  name="pan"
                  type="text"
                  isRequired={true}
                  isUpperCase={true}
                  placeholder="Enter PAN"
                  rules={panRules}
                  disabled={isPanVerified}
                />
              </Col>
              <Col xs={24} md={12}>
                <CustomInput
                  label="GSTIN"
                  name="gstin"
                  type="text"
                  isRequired={true}
                  isUpperCase={true}
                  disabled={isGstinVerified}
                  placeholder="Enter GSTIN"
                  rules={gstinRules}
                />
              </Col>
              {(!isPanVerified || !isGstinVerified) && (
                <>
                  <Divider />

                  <Flex
                    justify="space-between"
                    align="center"
                    wrap="wrap"
                    gap={16}
                    className="verification-footer"
                  >
                    <Typography.Text className="verify-note">
                      {`Complete ${isPanVerified ? "GSTIN" : "GSTIN & PAN"} verification ${isPanVerified ? "" : "with a single click"}. Click
                  "Verify ${isPanVerified ? "GSTIN" : "Both PAN & GSTIN"}" to proceed.`}
                    </Typography.Text>

                    <CustomButton
                      variant="outlined"
                      color="green"
                      onClick={handleVerifyBoth}
                      loading={panLoading || gstinLoading}
                      disabled={isPanVerified && isGstinVerified}
                    >
                      {`Verify ${isPanVerified ? "GSTIN" : "Both PAN & GSTIN"}`}
                    </CustomButton>
                  </Flex>
                </>
              )}
            </Row>
          </Col>

          {approveData?.primaryAddress?.addressId && (
            <Col xs={24}>
              <div
                style={{
                  background: "#F1F5FA",
                  borderRadius: 4,
                  padding: 20,
                  marginBottom: 20,
                }}
              >
                <Descriptions
                  title={TITLE_TXT.PRIM_ADD}
                  items={primaryAddItems}
                />
              </div>
            </Col>
          )}
          <Col xs={24}>
            <Checkbox
              style={{ marginBottom: 10 }}
              checked={isCommSame}
              onChange={handleCommSameChange}
            >
              Is communication Address same as Primary Address
            </Checkbox>
          </Col>
          <Col xs={24}>
            <div style={{ fontWeight: 500, fontSize: 18, paddingBottom: 10 }}>
              <Text>{TITLE_TXT.COMM_ADD}</Text>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="Address 1"
              name="commAdd1"
              type="text"
              isRequired={true}
              disabled={isCommSame}
              placeholder="Address 1"
              rules={address1Rules}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="Address 2"
              name="commAdd2"
              type="text"
              isRequired={true}
              disabled={isCommSame}
              placeholder="Address 2"
              rules={address2Rules}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="City"
              name="commCity"
              type="text"
              isRequired={true}
              disabled={isCommSame}
              placeholder="Enter City"
              rules={cityRules}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="State"
              name="commState"
              type="text"
              isRequired={true}
              disabled={isCommSame}
              placeholder="Enter State"
              rules={stateRules}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="Pincode"
              name="commPincode"
              type="text"
              isRequired={true}
              disabled={isCommSame}
              placeholder="Enter Pincode"
              rules={pincodeRules}
            />
          </Col>
          <Col xs={24} md={12}></Col>
          <Col xs={24} md={12}>
            <CustomDatePicker
              label="Incorporation Date"
              name="incorporationDate"
              isRequired={true}
              placeholder="Select Incorporation Date"
              rules={[{ required: true, message: ERROR_MSGS.ENTER_INC_DATE }]}
              disabledDate={(current) => current && current >= dayjs().startOf("day")}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="Enterprise Website"
              name="entWebsite"
              type="text"
              removeHttps={true}
              placeholder="Enter Enterprise Website"
              rules={websiteRules}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomSelect
              label="Rating"
              name="rating"
              placeholder="Select Rating"
              options={ratingData?.response?.map((rating) => ({
                value: rating.ratingId,
                label: capitalizeWords(rating.ratingDesc),
              }))}
              rules={[{ required: true, message: ERROR_MSGS.SEL_RATE_ERR_MSG }]}
            />
          </Col>
          <Col xs={24} md={12}>
            <CustomInput
              label="Enterprise Exposure Amount"
              name="creditExposure"
              isRequired={true}
              allowComma={true}
              placeholder="Enter Enterprise Exposure Amount"
              rules={[
                {
                  required: true,
                  message: ERROR_MSGS.EXPOSURE_AMT_ERR_MSG,
                },
                {
                  validator: (_, value) => {
                    // Let required rule handle empty values
                    if (!value) {
                      return Promise.resolve();
                    }

                    const numericValue = value?.replace(/,/g, "");

                    if (Number(numericValue) <= 0) {
                      return Promise.reject(
                        new Error("Exposure amount must be greater than 0"),
                      );
                    }

                    if (String(numericValue).length > 15) {
                      return Promise.reject(
                        new Error("Exposure amount cannot exceed 15 digits"),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Col>
          <Col xs={24}>
            <CustomInput
              label="Enterprise Description (Max 500 chars)"
              name="entDesc"
              rows={3}
              isTextArea={true}
              type="text"
              placeholder="Enter your Enterprise Description"
              rules={descriptionRules("Enterprise")}
            />
          </Col>
          <Col xs={24}>
            <CustomInput
              label="Financial Summary (Max 500 chars)"
              name="finSummary"
              rows={3}
              isTextArea={true}
              type="text"
              placeholder="Enter your Financial Summary"
              rules={financialSummaryRules}
            />
          </Col>
          <Col xs={24}>
            <CustomInput
              label="Comments (Max 500 chars)"
              name="comments"
              rows={3}
              isTextArea={true}
              type="text"
              placeholder="Enter your Comments"
              rules={CommentsRules}
            />
          </Col>
        </Row>

        <Row gutter={[24, 24]}>
          {filteredFiles()?.map((doc) => (
            <Col xs={24} md={12} key={doc.key}>
              <ApproveEntDocUploadCard
                fileTitle={doc.title}
                fileType={doc.type}
                fileKey={doc.key}
                isReview
                fileData={mapKycData(doc.key)}
                entId={entId}
                onFileChange={(file) => onFileChange(doc.key, file)}
                {...(doc.key === "mou" ? { mouChecked, setMouChecked } : {})}
              />
            </Col>
          ))}
        </Row>

        <FormFooter
          onCancelClick={() => {
            onClose();
            form.resetFields();
          }}
          submitLoading={approveSubmitLoading}
          btnName={BTN_TXT.SUBMIT}
          error={error || panError || gstinError}
        />
      </Form>
    </Spin>
  );
};

export default ApproveEntForm;
